import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { listChatbots, listWhatsappNumbers } from '@/features/chatbot/api';
import { listAccessGroups } from '@/features/configuracoes/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMediaUrlById, type MediaType } from '@/features/media-library/api';
import { MidiaDoCabecalhoPicker } from '@/features/media-library/MidiaDoCabecalhoPicker';
import { ChevronLeft, ChevronRight, ExternalLink, FileText, Loader2, Save, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  type CampaignDraftState,
  type CampaignImportRow,
  type ContactPickerRow,
  type RecipientFilterSelection,
  type RecipientsView,
  type TemplateRow,
  type VariableMapping,
  createCampaign,
  createCampaignDraft,
  deleteCampaignDraft,
  listApprovedTemplates,
  listContactTags,
  updateCampaignDraft,
} from './api';
import { RecipientsStep } from './RecipientsStep';
import { botoesDinamicosDeUrl, prefixoDaUrl } from '@/features/atendimento/lib/template-botoes';

interface Props {
  onClose: () => void;
  /** Quando aberto a partir de um rascunho: id + estado salvo pra prefill. */
  draftId?: number;
  /** Prefill do wizard — rascunho salvo ou cópia de uma campanha (sem draftId). */
  initialState?: CampaignDraftState;
  /** Título do cabeçalho (padrão "Nova campanha WABA"). */
  title?: string;
}

type Step = 1 | 2 | 3 | 4 | 5;

const STEP_LABELS: Record<Step, string> = {
  1: 'Canal',
  2: 'Template',
  3: 'Destinatários',
  4: 'Respostas',
  5: 'Agendamento',
};

// Janela de resposta — tempo em que a resposta do contato à campanha é
// roteada conforme o "destino da resposta". Valor em minutos.
const RESPONSE_WINDOW_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 240, label: '4 horas' },
  { value: 720, label: '12 horas' },
  { value: 1440, label: '24 horas' },
  { value: 2880, label: '2 dias' },
  { value: 5760, label: '4 dias' },
  { value: 10080, label: '1 semana' },
];

type ResponseDestination = 'channel_default' | 'chatbot';

// Intervalo entre o envio de uma mensagem e a próxima (throttle da campanha).
// 0 = imediato (sem espera). Em segundos, 0 a 60.
const MESSAGE_DELAY_MAX = 60;

// Modo de envio (etapa final). manual = cria a campanha parada, dispara só
// quando apertar o play na lista. now = dispara já. scheduled = data/hora.
type SendMode = 'manual' | 'now' | 'scheduled';

// Opções de campo do contato pra variáveis dinâmicas (por destinatário).
const FIELD_OPTIONS: Array<{ path: string; label: string }> = [
  { path: 'contact.display_name', label: 'Nome do contato' },
  { path: 'contact.email', label: 'Email do contato' },
  { path: 'contact.state_uf', label: 'UF do contato' },
];

function fieldLabel(path: string): string {
  return FIELD_OPTIONS.find((f) => f.path === path)?.label ?? path;
}

// Extrai o formato do header de mídia do template (IMAGE/VIDEO/DOCUMENT) ou
// null se o header é texto / não existe. Usado pra decidir se mostra o picker
// de mídia da galeria no wizard.
function headerMediaFormatOf(template: TemplateRow | undefined): MediaType | null {
  if (!template) return null;
  const comps = (template.components ?? []) as Array<{ type?: string; format?: string }>;
  const header = comps.find((c) => (c.type ?? '').toUpperCase() === 'HEADER');
  const fmt = (header?.format ?? 'TEXT').toUpperCase();
  if (fmt === 'IMAGE') return 'image';
  if (fmt === 'VIDEO') return 'video';
  if (fmt === 'DOCUMENT') return 'document';
  return null;
}

// Badge de categoria do template HSM (Meta). Cores distintas pra leitura
// rápida: Marketing coral/vermelho (promocional, mais caro), Utilidade verde
// (transacional, mais barato), Autenticação azul. Pill arredondado, case
// normal, peso semibold — estilo de referência do app.
const CATEGORY_STYLE: Record<string, { label: string; classes: string }> = {
  MARKETING: { label: 'Marketing', classes: 'bg-danger-50 text-danger-600' },
  UTILITY: { label: 'Utilidade', classes: 'bg-success-50 text-success-700' },
  AUTHENTICATION: { label: 'Autenticação', classes: 'bg-brand-50 text-brand-700' },
};

function CategoryBadge({ category }: { category: string }) {
  const c = CATEGORY_STYLE[(category ?? '').toUpperCase()] ?? {
    label: category,
    classes: 'bg-n-100 text-n-700',
  };
  return (
    <span className={`rounded-pill px-2.5 py-0.5 font-semibold text-[11px] ${c.classes}`}>
      {c.label}
    </span>
  );
}

export function CampaignWizardModal({
  onClose,
  draftId: initialDraftId,
  initialState,
  title = 'Nova campanha WABA',
}: Props) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>((initialState?.step as Step) ?? 1);

  const [name, setName] = useState(initialState?.name ?? '');
  const [whatsappNumberId, setWhatsappNumberId] = useState<number | null>(
    initialState?.whatsappNumberId ?? null,
  );
  const [templateId, setTemplateId] = useState<number | null>(initialState?.templateId ?? null);
  const [selectedContacts, setSelectedContacts] = useState<ContactPickerRow[]>(
    initialState?.selectedContacts ?? [],
  );
  // Seleção por filtro ("todos os N do filtro") — exclusiva com selectedContacts.
  // Guarda a regra; o backend resolve os contatos no disparo (escala 30k+).
  const [filterSelection, setFilterSelection] = useState<RecipientFilterSelection | null>(
    initialState?.filterSelection ?? null,
  );
  // Lista de upload (CSV) — números crus, só na memória do wizard. Materializa
  // no backend só na finalização. Persiste no rascunho; some ao sair sem salvar.
  const [uploadRows, setUploadRows] = useState<CampaignImportRow[]>(initialState?.uploadRows ?? []);
  const [variables, setVariables] = useState<Record<string, VariableMapping>>(
    initialState?.variables ?? {},
  );
  const [scheduledAt, setScheduledAt] = useState(initialState?.scheduledAt ?? '');
  // Modo de envio (etapa final). Default 'manual' (seguro — não dispara sozinho).
  const [sendMode, setSendMode] = useState<SendMode>(initialState?.sendMode ?? 'manual');
  // Intervalo entre mensagens (throttle). 0 = imediato.
  const [messageDelaySeconds, setMessageDelaySeconds] = useState<number>(
    initialState?.messageDelaySeconds ?? 0,
  );
  // Etapa "Respostas": janela (minutos) + destino do roteamento da resposta.
  const [responseWindowMinutes, setResponseWindowMinutes] = useState<number>(
    initialState?.responseWindowMinutes ?? 2880,
  );
  const [responseDestination, setResponseDestination] = useState<ResponseDestination>(
    initialState?.responseDestination ?? 'channel_default',
  );
  // Chatbot específico (URA) — só usado quando destino = 'chatbot'.
  const [responseChatbotId, setResponseChatbotId] = useState<number | null>(
    initialState?.responseChatbotId ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  // Id do rascunho em edição (null = ainda não salvo). Salvar cria/atualiza.
  const [draftId, setDraftId] = useState<number | null>(initialDraftId ?? null);
  const [savingDraft, setSavingDraft] = useState(false);
  // View do passo Destinatários (modo + filtros) — reportada pelo RecipientsStep
  // pra salvar no rascunho e reabrir na mesma tela.
  const [recipientsView, setRecipientsView] = useState<RecipientsView | undefined>(
    initialState?.recipientsView,
  );
  // Header de mídia escolhido da galeria (quando o template tem header
  // IMAGE/VIDEO/DOCUMENT). Frontend pronto — o envio do header ainda depende
  // de suporte no backend (campaigns.header_media_id + worker). Guardamos
  // o id + url só pro preview por enquanto.
  const [headerMediaId, setHeaderMediaId] = useState<number | null>(
    initialState?.headerMediaId ?? null,
  );
  // Nome da mídia — só pra prévia (o cartão do documento mostra o arquivo).
  const [headerMediaName, setHeaderMediaName] = useState<string | null>(
    initialState?.headerMediaName ?? null,
  );
  const [headerMediaUrl, setHeaderMediaUrl] = useState<string | null>(null);

  const numbersQ = useQuery({ queryKey: ['whatsapp-numbers'], queryFn: listWhatsappNumbers });
  // Templates são por WABA na Meta — filtra pelos do canal escolhido no passo 1
  // (o backend resolve whatsapp_number_id → account_id). Só busca com canal definido.
  const templatesQ = useQuery({
    queryKey: ['templates', whatsappNumberId],
    queryFn: () => listApprovedTemplates(whatsappNumberId ?? undefined),
    enabled: whatsappNumberId != null,
  });
  const tagsQ = useQuery({ queryKey: ['contact-tags'], queryFn: listContactTags });
  const groupsQ = useQuery({
    queryKey: ['access-groups'],
    queryFn: listAccessGroups,
    staleTime: 60_000,
  });
  const chatbotsQ = useQuery({ queryKey: ['chatbots'], queryFn: listChatbots });
  const activeChatbots = useMemo(
    () => (chatbotsQ.data ?? []).filter((c) => c.status === 'active'),
    [chatbotsQ.data],
  );

  const selectedTemplate = useMemo<TemplateRow | undefined>(
    () => (templatesQ.data ?? []).find((t) => t.id === templateId),
    [templatesQ.data, templateId],
  );

  const approvedTemplates = useMemo(
    () => (templatesQ.data ?? []).filter((t) => t.status === 'approved'),
    [templatesQ.data],
  );

  // Variáveis pra prévia do passo 3: sobrepõe os valores da 1ª linha da planilha
  // (se houver upload com colunas) sobre o valor do passo 2 — mostra a mensagem
  // real que o 1º destinatário receberia. Sem upload, é igual ao preview do passo 2.
  const previewVariables = useMemo<Record<string, VariableMapping>>(() => {
    const first = uploadRows[0];
    if (!first?.variables || Object.keys(first.variables).length === 0) return variables;
    const merged: Record<string, VariableMapping> = { ...variables };
    for (const [slot, val] of Object.entries(first.variables)) {
      merged[slot] = { kind: 'fixed', value: val };
    }
    return merged;
  }, [variables, uploadRows]);
  const previewFromUpload = Boolean(
    uploadRows[0]?.variables && Object.keys(uploadRows[0].variables).length > 0,
  );

  // Botões de URL dinâmica do template — cada um exige o valor do {{N}} do link
  // (chave `btn:<index>`), senão a Meta recusa com 131008. Índice = posição no
  // array de botões (bate com o buildMetaComponents do dispatcher).
  const botoesDeUrl = useMemo(
    () => botoesDinamicosDeUrl(selectedTemplate?.components),
    [selectedTemplate],
  );

  // variables_map enviado = os slots do template atual (1..variableCount) MAIS
  // as chaves `btn:<index>` dos botões de URL. Poda chaves antigas que sobraram
  // no estado ao trocar de template — o backend aceita variável a mais, mas
  // enviar lixo de um template anterior confunde a leitura.
  const variablesMapForSubmit = useMemo<Record<string, VariableMapping>>(() => {
    const vc = selectedTemplate?.variableCount ?? 0;
    const out: Record<string, VariableMapping> = {};
    for (let n = 1; n <= vc; n++) {
      const m = variables[String(n)];
      if (m) out[String(n)] = m;
    }
    for (const b of botoesDeUrl) {
      const m = variables[`btn:${b.index}`];
      if (m) out[`btn:${b.index}`] = m;
    }
    return out;
  }, [variables, selectedTemplate, botoesDeUrl]);

  // Formato do header de mídia do template selecionado (image/video/document)
  // ou null se header é texto. Decide se o picker de mídia aparece.
  const headerMediaFormat = useMemo(
    () => headerMediaFormatOf(selectedTemplate),
    [selectedTemplate],
  );

  // Ao trocar de template, limpa só a mídia do header (a anterior pode não fazer
  // sentido pro novo). A LISTA de upload é preservada — os parametro_N casam por
  // número do slot ({{N}}), então a mesma lista se relaciona com qualquer
  // template (o que passar do que o novo template usa é ignorado no envio).
  // Pula a 1ª execução (montagem) pra não apagar o que foi restaurado de um rascunho.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setHeaderMediaId(null);
    setHeaderMediaUrl(null);
    setHeaderMediaName(null);
  }, [templateId]);

  // Se trocar o canal (passo 1), o template selecionado pode ser de outra WABA
  // e sumir da lista filtrada — nesse caso limpa a seleção pra forçar re-escolher
  // um template válido pro novo canal. (templatesQ.data fica undefined durante o
  // refetch da nova key, então não limpa prematuramente.)
  useEffect(() => {
    if (
      templateId != null &&
      templatesQ.data &&
      !templatesQ.data.some((t) => t.id === templateId)
    ) {
      setTemplateId(null);
    }
  }, [templatesQ.data, templateId]);

  // Resolve a signed URL da mídia restaurada (rascunho guarda só o id).
  useEffect(() => {
    if (headerMediaId != null && headerMediaUrl == null) {
      getMediaUrlById(headerMediaId)
        .then((r) => setHeaderMediaUrl(r.url))
        .catch(() => {});
    }
  }, [headerMediaId, headerMediaUrl]);

  // Destinatários: contatos (handpick) + lista de upload (números crus) OU
  // seleção por filtro ("todos os N" — backend resolve no disparo). Mutuamente
  // exclusivos no RecipientsStep; a contagem efetiva pega o que estiver ativo.
  const recipientCount = filterSelection
    ? filterSelection.count
    : selectedContacts.length + uploadRows.length;

  // Snapshot do estado atual do wizard pra salvar como rascunho. headerMediaUrl
  // NÃO entra (signed URL expira; re-resolvemos do id ao reabrir).
  const buildDraftState = (): CampaignDraftState => ({
    step,
    name,
    whatsappNumberId,
    templateId,
    variables,
    headerMediaId,
    headerMediaName,
    selectedContacts,
    filterSelection,
    uploadRows,
    scheduledAt,
    sendMode,
    messageDelaySeconds,
    responseWindowMinutes,
    responseDestination,
    responseChatbotId,
    ...(recipientsView && { recipientsView }),
  });

  const saveDraft = async (): Promise<void> => {
    setSavingDraft(true);
    setError(null);
    try {
      const draftName = name.trim() || 'Rascunho sem nome';
      const state = buildDraftState();
      if (draftId != null) {
        await updateCampaignDraft(draftId, draftName, state);
      } else {
        const { id } = await createCampaignDraft(draftName, state);
        setDraftId(id);
      }
      qc.invalidateQueries({ queryKey: ['campaign-drafts'] });
      toast.success('Rascunho salvo', { description: 'Você pode continuar depois.' });
    } catch (err) {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
      else setError('Falha ao salvar rascunho');
    } finally {
      setSavingDraft(false);
    }
  };

  const createMut = useMutation({
    mutationFn: () =>
      createCampaign({
        name: name.trim(),
        template_id: templateId!,
        whatsapp_number_id: whatsappNumberId!,
        // variables_map = valores do passo 2 (fixo/token) SÓ dos slots do template
        // atual (podado), que são o FALLBACK. A personalização por linha vai em
        // upload_recipients[].variables e o dispatcher SOBREPÕE por linha (só
        // valores não-vazios → coluna vence, célula vazia mantém o passo 2).
        variables_map: variablesMapForSubmit,
        contact_ids: selectedContacts.map((c) => c.id),
        // Lista de upload: o backend separa quem já é contato (fluxo normal) de
        // quem é número novo (destinatário "cru"; contato nasce só na resposta).
        ...(uploadRows.length > 0 && {
          upload_recipients: uploadRows.map((r) => ({
            phone: r.phone,
            name: r.display_name,
            ...(r.variables && { variables: r.variables }),
          })),
        }),
        send_mode: sendMode,
        message_delay_seconds: messageDelaySeconds,
        response_window_minutes: responseWindowMinutes,
        response_destination: responseDestination,
        ...(responseDestination === 'chatbot' &&
          responseChatbotId != null && { response_chatbot_id: responseChatbotId }),
        scheduled_at:
          sendMode === 'scheduled' && scheduledAt ? new Date(scheduledAt).toISOString() : null,
        ...(headerMediaId != null && { header_media_id: headerMediaId }),
        // Seleção por filtro: manda a regra (não os 30k ids). Backend resolve.
        ...(filterSelection && {
          recipient_filter: {
            tag_ids: filterSelection.tagIds,
            access_group_ids: filterSelection.accessGroupIds,
            ...(filterSelection.q && { q: filterSelection.q }),
            ...(filterSelection.from && { last_inbound_from: filterSelection.from }),
            ...(filterSelection.to && { last_inbound_to: filterSelection.to }),
          },
        }),
      }),
    onSuccess: async () => {
      // Campanha criada de verdade → descarta o rascunho.
      if (draftId != null) {
        try {
          await deleteCampaignDraft(draftId);
        } catch {
          /* rascunho órfão é inofensivo */
        }
      }
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign-drafts'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.problem.detail ?? err.problem.title);
    },
  });

  const canNext = (): boolean => {
    if (step === 1) return !!name.trim() && !!whatsappNumberId;
    if (step === 2) {
      if (!selectedTemplate) return false;
      // Header de mídia exige uma mídia escolhida — senão o Meta rejeita o envio.
      if (headerMediaFormat && headerMediaId == null) return false;
      // Template escolhido + todas as variáveis preenchidas (kind setado, e
      // se for fixo, valor não-vazio). Sem isso, deixa o atendente avançar
      // sem completar a personalização.
      const count = selectedTemplate.variableCount;
      for (let n = 1; n <= count; n++) {
        const m = variables[String(n)];
        if (!m) return false;
        if (m.kind === 'fixed' && !m.value.trim()) return false;
      }
      // Link de cada botão de URL dinâmica também é obrigatório — sem ele a Meta
      // recusa o envio (131008).
      for (const b of botoesDeUrl) {
        const m = variables[`btn:${b.index}`];
        if (!m) return false;
        if (m.kind === 'fixed' && !m.value.trim()) return false;
      }
      return true;
    }
    if (step === 3) return recipientCount > 0;
    if (step === 4) {
      // Respostas: destino "URA" exige escolher um chatbot. Tempo tem default.
      if (responseDestination === 'chatbot' && responseChatbotId == null) return false;
      return true;
    }
    if (step === 5) {
      // Agendar exige data; manual/agora não exigem nada.
      if (sendMode === 'scheduled') return !!scheduledAt;
      return true;
    }
    return false;
  };

  return (
    // Clicar FORA não fecha (pedido do dono, 14/out/2026): montar a campanha dá
    // trabalho e um clique escorregado no fundo perdia tudo. Fecha só pelo X ou Esc.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-md bg-n-0 shadow-xl">
        <header className="flex items-center justify-between border-n-200 border-b px-6 py-4">
          <h2 className="font-bold text-[16px] text-n-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-n-500 hover:text-n-700"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </header>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 border-n-200 border-b bg-n-50 px-6 py-3 text-[12px]">
          {([1, 2, 3, 4, 5] as Step[]).map((s) => (
            <div key={s} className="flex items-center gap-2">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full font-bold ${
                  step >= s ? 'bg-brand-500 text-white' : 'bg-n-200 text-n-600'
                }`}
              >
                {s}
              </span>
              <span className={step === s ? 'font-semibold text-n-900' : 'text-n-500'}>
                {STEP_LABELS[s]}
              </span>
              {s < 5 && <ChevronRight size={14} className="text-n-400" />}
            </div>
          ))}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-6 text-[13px]">
          {step === 1 && (
            <>
              <label className="block">
                <span className="mb-1 block font-semibold text-[12px] text-n-700">
                  Nome da campanha
                </span>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Promoção Abril"
                />
              </label>
              <label className="block">
                <span className="mb-1 block font-semibold text-[12px] text-n-700">
                  Número WhatsApp
                </span>
                <select
                  value={whatsappNumberId ?? ''}
                  onChange={(e) =>
                    setWhatsappNumberId(e.target.value ? Number(e.target.value) : null)
                  }
                  className="h-10 w-full rounded-md border border-n-200 bg-n-0 px-3 text-[13px]"
                >
                  <option value="">— Selecione —</option>
                  {/* Só canais Cloud API (meta_official) — campanhas usam
                      template HSM, que não existe em canal QR code. */}
                  {(numbersQ.data ?? [])
                    .filter((n) => n.channel_type === 'meta_official')
                    .map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.display_name
                          ? `${n.display_name} · ${n.display_number}`
                          : n.display_number}
                      </option>
                    ))}
                </select>
              </label>
            </>
          )}

          {step === 2 && (
            <>
              <label className="block">
                <span className="mb-1 block font-semibold text-[12px] text-n-700">
                  Template aprovado
                </span>
                <select
                  value={templateId ?? ''}
                  onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)}
                  className="h-10 w-full rounded-md border border-n-200 bg-n-0 px-3 text-[13px]"
                >
                  <option value="">— Selecione —</option>
                  {approvedTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ·{' '}
                      {CATEGORY_STYLE[(t.category ?? '').toUpperCase()]?.label ?? t.category}
                    </option>
                  ))}
                </select>
                {approvedTemplates.length === 0 && !templatesQ.isLoading && (
                  <span className="mt-1 block text-[11px] text-amber-600">
                    Nenhum template aprovado para este canal. Sincronize em Configurações →
                    Templates.
                  </span>
                )}
              </label>

              {/* Mídia do header — aparece quando o template tem header
                  IMAGE/VIDEO/DOCUMENT. Da Biblioteca ou enviada do computador
                  ali mesmo (sem precisar ir na Biblioteca antes). */}
              {selectedTemplate && headerMediaFormat && headerMediaFormat !== 'audio' && (
                <MidiaDoCabecalhoPicker
                  type={headerMediaFormat}
                  dica="A mídia escolhida vai no cabeçalho de cada mensagem enviada."
                  selectedId={headerMediaId}
                  selectedLabel={headerMediaName}
                  onSelect={(media) => {
                    setHeaderMediaId(media.id);
                    // URL da prévia vem do efeito que busca quando o id muda.
                    setHeaderMediaUrl(null);
                    setHeaderMediaName(media.label || media.original_name || null);
                  }}
                  onClear={() => {
                    setHeaderMediaId(null);
                    setHeaderMediaUrl(null);
                    setHeaderMediaName(null);
                  }}
                />
              )}

              {/* Inputs de variáveis — aparecem quando o template tem {{N}}.
                  Ficam ACIMA do preview: atendente preenche aqui e vê o
                  resultado refletido na bolha logo abaixo. Por enquanto só
                  valor fixo (igual pra todos). Personalização por contato
                  (campo dinâmico) virá depois, com explicação dedicada. */}
              {selectedTemplate && selectedTemplate.variableCount > 0 && (
                <div className="space-y-2">
                  <span className="block font-semibold text-[12px] text-n-700">
                    Preencher variáveis
                  </span>
                  {Array.from({ length: selectedTemplate.variableCount }, (_, i) => i + 1).map(
                    (n) => {
                      const m = variables[String(n)];
                      const value = m?.kind === 'fixed' ? m.value : '';
                      return (
                        <label
                          key={n}
                          className="flex items-center gap-2 rounded border border-n-200 bg-n-0 p-3 text-[12px]"
                        >
                          <span className="flex-shrink-0 rounded bg-amber-100 px-1.5 py-0.5 font-mono font-bold text-amber-800">{`{{${n}}}`}</span>
                          <Input
                            value={value}
                            onChange={(e) =>
                              setVariables((prev) => ({
                                ...prev,
                                [String(n)]: { kind: 'fixed', value: e.target.value },
                              }))
                            }
                            placeholder="Texto fixo ou {{firstname}}"
                          />
                        </label>
                      );
                    },
                  )}
                  <p className="leading-relaxed text-[11px] text-n-500">
                    💡 Personalize por contato:{' '}
                    <span className="font-mono text-amber-800">{'{{firstname}}'}</span>,{' '}
                    <span className="font-mono text-amber-800">{'{{name}}'}</span>,{' '}
                    <span className="font-mono text-amber-800">{'{{phone}}'}</span>,{' '}
                    <span className="font-mono text-amber-800">{'{{email}}'}</span>,{' '}
                    <span className="font-mono text-amber-800">{'{{state_uf}}'}</span>,{' '}
                    <span className="font-mono text-amber-800">{'{{custom.CAMPO}}'}</span> — cada
                    destinatário recebe com os próprios dados.
                  </p>
                </div>
              )}

              {/* Link dos botões de URL dinâmica — separado das variáveis do
                  body (o {{N}} do botão numera à parte na Meta). Fica FORA do
                  gate `variableCount > 0`: um template pode ter botão dinâmico
                  sem nenhuma variável no texto. */}
              {selectedTemplate && botoesDeUrl.length > 0 && (
                <div className="space-y-2">
                  <span className="block font-semibold text-[12px] text-n-700">
                    Link dos botões
                  </span>
                  {botoesDeUrl.map((b) => {
                    const key = `btn:${b.index}`;
                    const m = variables[key];
                    const value = m?.kind === 'fixed' ? m.value : '';
                    return (
                      <label
                        key={b.index}
                        className="flex flex-col gap-1 rounded border border-n-200 bg-n-0 p-3 text-[12px]"
                      >
                        <span className="text-[11px] text-n-600">
                          Botão <strong>{b.text}</strong>
                        </span>
                        <div className="flex items-center overflow-hidden rounded border border-n-200">
                          <span className="max-w-[52%] flex-shrink-0 truncate border-n-200 border-r bg-n-50 px-2 py-1 font-mono text-[11px] text-n-400">
                            {prefixoDaUrl(b.url)}
                          </span>
                          <Input
                            value={value}
                            onChange={(e) =>
                              setVariables((prev) => ({
                                ...prev,
                                [key]: { kind: 'fixed', value: e.target.value },
                              }))
                            }
                            placeholder="valor do link (fixo ou {{custom.CAMPO}})"
                            className="border-0"
                          />
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {selectedTemplate ? (
                <TemplatePreview
                  template={selectedTemplate}
                  variables={variables}
                  headerMediaOverride={headerMediaUrl}
                  headerMediaName={headerMediaName}
                />
              ) : (
                <div className="rounded-md border border-n-200 border-dashed bg-n-50 px-4 py-8 text-center text-[12px] text-n-500">
                  Selecione um template pra ver o preview.
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <RecipientsStep
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
                filterSelection={filterSelection}
                setFilterSelection={setFilterSelection}
                uploadRows={uploadRows}
                setUploadRows={setUploadRows}
                tags={tagsQ.data ?? []}
                accessGroups={groupsQ.data ?? []}
                templateVariableCount={selectedTemplate?.variableCount ?? 0}
                {...(recipientsView && { initialView: recipientsView })}
                onViewChange={setRecipientsView}
              />
              {/* Prévia da mensagem (bolha WhatsApp) — só aparece DEPOIS de subir
                  a planilha, personalizada com os valores da 1ª linha. */}
              {selectedTemplate && uploadRows.length > 0 && (
                <div className="border-n-200 border-t pt-4">
                  <span className="mb-2 block font-semibold text-[12px] text-n-700">
                    Prévia da mensagem
                    {previewFromUpload && (
                      <span className="ml-1 font-normal text-[11px] text-n-500">
                        (1ª linha da planilha)
                      </span>
                    )}
                  </span>
                  <TemplatePreview
                    template={selectedTemplate}
                    variables={previewVariables}
                    headerMediaOverride={headerMediaUrl}
                    headerMediaName={headerMediaName}
                  />
                </div>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <label className="block">
                <span className="mb-1 block font-semibold text-[12px] text-n-700">
                  Tempo de resposta <span className="text-brand-600">*</span>
                </span>
                <select
                  value={responseWindowMinutes}
                  onChange={(e) => setResponseWindowMinutes(Number(e.target.value))}
                  className="h-10 w-full rounded-md border border-n-200 bg-n-0 px-3 text-[13px]"
                >
                  {RESPONSE_WINDOW_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-[11px] text-n-500">
                  Janela em que a resposta do contato a esta campanha é roteada conforme o destino
                  abaixo.
                </span>
              </label>

              <label className="block">
                <span className="mb-1 block font-semibold text-[12px] text-n-700">
                  Destino da resposta <span className="text-brand-600">*</span>
                </span>
                <select
                  value={responseDestination}
                  onChange={(e) => setResponseDestination(e.target.value as ResponseDestination)}
                  className="h-10 w-full rounded-md border border-n-200 bg-n-0 px-3 text-[13px]"
                >
                  <option value="channel_default">Padrão do canal</option>
                  <option value="chatbot">Enviar para o ChatBot</option>
                </select>
                <span className="mt-1 block text-[11px] text-n-500">
                  {responseDestination === 'channel_default'
                    ? 'Vai para o canal vinculado ao envio — se houver chatbot configurado nele, segue esse fluxo.'
                    : 'Envia para um chatbot específico, escolhido abaixo.'}
                </span>
              </label>

              {responseDestination === 'chatbot' && (
                <label className="block">
                  <span className="mb-1 block font-semibold text-[12px] text-n-700">
                    Chatbot <span className="text-brand-600">*</span>
                  </span>
                  <select
                    value={responseChatbotId ?? ''}
                    onChange={(e) =>
                      setResponseChatbotId(e.target.value ? Number(e.target.value) : null)
                    }
                    className="h-10 w-full rounded-md border border-n-200 bg-n-0 px-3 text-[13px]"
                  >
                    <option value="">— Selecione —</option>
                    {activeChatbots.map((cb) => (
                      <option key={cb.id} value={cb.id}>
                        {cb.name}
                      </option>
                    ))}
                  </select>
                  {activeChatbots.length === 0 && !chatbotsQ.isLoading && (
                    <span className="mt-1 block text-[11px] text-amber-600">
                      Nenhum chatbot ativo. Crie/ative um em Chatbot.
                    </span>
                  )}
                </label>
              )}
            </>
          )}

          {step === 5 && (
            <div className="space-y-4">
              {/* Configurações — antes do formato de envio */}
              <div>
                <span className="mb-2 block font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
                  Configurações
                </span>
                <label className="block">
                  <span className="mb-1 block font-semibold text-[12px] text-n-700">
                    Intervalo entre mensagens
                  </span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={MESSAGE_DELAY_MAX}
                      value={messageDelaySeconds}
                      onChange={(e) => {
                        const n = Math.floor(Number(e.target.value));
                        setMessageDelaySeconds(
                          Number.isFinite(n) ? Math.max(0, Math.min(MESSAGE_DELAY_MAX, n)) : 0,
                        );
                      }}
                      className="h-10 w-[90px] rounded-md border border-n-200 bg-n-0 px-3 text-[13px]"
                    />
                    <span className="text-[13px] text-n-600">segundos</span>
                  </div>
                  <span className="mt-1 block text-[11px] text-n-500">
                    Espaço entre o envio de uma mensagem e a próxima (0 = imediato). Maior intervalo
                    protege o atendimento e a reputação do número.
                  </span>
                </label>
              </div>

              {/* Formato do envio */}
              <div className="border-n-200 border-t pt-4">
                <span className="mb-2 block font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
                  Formato do envio
                </span>
                <div className="space-y-2">
                  <SendModeOption
                    checked={sendMode === 'manual'}
                    onSelect={() => setSendMode('manual')}
                    title="Envio manual"
                    desc="A campanha fica pronta e só dispara quando você apertar o play na lista."
                  />
                  <SendModeOption
                    checked={sendMode === 'now'}
                    onSelect={() => setSendMode('now')}
                    title="Enviar agora"
                    desc="Dispara assim que você criar a campanha."
                  />
                  <SendModeOption
                    checked={sendMode === 'scheduled'}
                    onSelect={() => setSendMode('scheduled')}
                    title="Agendar envio"
                    desc="Dispara automaticamente na data e hora escolhidas."
                  >
                    {sendMode === 'scheduled' && (
                      <input
                        type="datetime-local"
                        value={scheduledAt}
                        onChange={(e) => setScheduledAt(e.target.value)}
                        className="mt-2 h-10 w-full rounded-md border border-n-200 bg-n-0 px-3 text-[13px]"
                      />
                    )}
                  </SendModeOption>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              {error}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 border-n-200 border-t bg-n-50 px-6 py-4">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(1, s - 1) as Step)}
            disabled={step === 1}
          >
            <ChevronLeft />
            Voltar
          </Button>
          <div className="flex items-center gap-2">
            {/* Salvar rascunho — disponível em qualquer passo, mesmo incompleto.
                Exige só um nome (pra identificar o rascunho na lista). */}
            <Button variant="ghost" onClick={() => void saveDraft()} disabled={savingDraft}>
              {savingDraft ? <Loader2 className="animate-spin" /> : <Save />}
              Salvar rascunho
            </Button>
            {step < 5 ? (
              <Button
                onClick={() => setStep((s) => Math.min(5, s + 1) as Step)}
                disabled={!canNext()}
              >
                Próximo
                <ChevronRight />
              </Button>
            ) : (
              <Button
                onClick={() => createMut.mutate()}
                disabled={!canNext() || createMut.isPending}
              >
                {createMut.isPending && <Loader2 className="animate-spin" />}
                {sendMode === 'now'
                  ? 'Disparar agora'
                  : sendMode === 'scheduled'
                    ? 'Agendar'
                    : 'Salvar campanha'}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

// ─── Opção de modo de envio (radio) ──────────────────────────────────────
function SendModeOption({
  checked,
  onSelect,
  title,
  desc,
  children,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  desc: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-md border p-3 transition-colors ${
        checked ? 'border-brand-300 bg-brand-50' : 'border-n-200 bg-n-0'
      }`}
    >
      <button type="button" onClick={onSelect} className="flex w-full items-start gap-3 text-left">
        <span
          className={`mt-0.5 flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full border-2 ${
            checked ? 'border-brand-500' : 'border-n-300'
          }`}
        >
          {checked && <span className="h-2.5 w-2.5 rounded-full bg-brand-500" />}
        </span>
        <span className="min-w-0">
          <span className="block font-semibold text-[13px] text-n-900">{title}</span>
          <span className="mt-0.5 block text-[11px] text-n-500">{desc}</span>
        </span>
      </button>
      {children && <div className="pl-[30px]">{children}</div>}
    </div>
  );
}

// ─── Preview visual do template (estilo bolha WhatsApp) ───────────────────
// Renderiza os components do template HSM (header/body/footer/buttons) num
// card que imita a bolha do WhatsApp. Variáveis {{N}} ficam destacadas pra
// o atendente entender o que será substituído. Substitui o JSON cru anterior.
interface TemplateComponent {
  type?: string;
  format?: string;
  text?: string;
  // Mídia de exemplo aprovada pela Meta (header IMAGE/VIDEO/DOCUMENT).
  example?: { header_handle?: string[]; header_text?: string[] };
  buttons?: Array<{ type?: string; text?: string; url?: string; phone_number?: string }>;
}

// Ícone unicode por tipo de botão (paridade visual com WhatsApp).
function buttonIcon(type: string): string {
  const t = (type ?? '').toUpperCase();
  if (t === 'URL') return '🔗';
  if (t === 'PHONE_NUMBER') return '📞';
  return '↩'; // QUICK_REPLY e afins
}

/** Bolha WhatsApp do template com as variáveis aplicadas. Exportada pra
 *  prévia da mensagem enviada na lista de campanhas (MensagemDaCampanhaModal). */
export function TemplatePreview({
  template,
  variables,
  headerMediaOverride,
  headerMediaName,
}: {
  template: TemplateRow;
  variables: Record<string, VariableMapping>;
  headerMediaOverride?: string | null;
  /** Nome do arquivo — aparece no cartão do cabeçalho de documento. */
  headerMediaName?: string | null;
}) {
  const components = (template.components ?? []) as TemplateComponent[];
  const header = components.find((c) => (c.type ?? '').toUpperCase() === 'HEADER');
  const body = components.find((c) => (c.type ?? '').toUpperCase() === 'BODY');
  const footer = components.find((c) => (c.type ?? '').toUpperCase() === 'FOOTER');
  const buttonsComp = components.find((c) => (c.type ?? '').toUpperCase() === 'BUTTONS');
  const headerFormat = (header?.format ?? 'TEXT').toUpperCase();
  const headerIsText = headerFormat === 'TEXT';
  // Mídia do header: prioriza a escolhida na galeria (override); senão usa a
  // de exemplo aprovada pela Meta (CDN). Fallback pra placeholder.
  const headerMediaUrl = headerMediaOverride ?? header?.example?.header_handle?.[0] ?? null;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-n-500">
        <span className="rounded bg-n-100 px-1.5 py-0.5 font-mono text-n-700">{template.name}</span>
        <CategoryBadge category={template.category} />
        <span>{template.language}</span>
        <span>·</span>
        <span>{template.variableCount} variáveis</span>
      </div>
      {/* Bolha estilo WhatsApp (verde claro como outbound). force-light: o verde
          é fixo e não inverte no dark — sem isso, text-n-900 vira ~branco no
          tema escuro e some no fundo verde. */}
      <div className="force-light overflow-hidden rounded-lg bg-[#dcf8c6] text-[13px] text-n-900 shadow-sm">
        {/* Header de mídia: renderiza a imagem/vídeo de exemplo real. */}
        {header && !headerIsText && (
          <div className="bg-black/5">
            {headerFormat === 'IMAGE' && headerMediaUrl ? (
              <img
                src={headerMediaUrl}
                alt="Cabeçalho do template"
                className="max-h-56 w-full object-cover"
              />
            ) : headerFormat === 'VIDEO' && headerMediaUrl ? (
              // biome-ignore lint/a11y/useMediaCaption: preview de template, sem legenda
              <video src={headerMediaUrl} controls className="max-h-56 w-full" />
            ) : headerFormat === 'DOCUMENT' && headerMediaUrl ? (
              // Documento: cartão com o nome + link que abre numa ABA NOVA (o
              // navegador mostra o PDF no visualizador dele). Não usa o
              // PdfPreviewModal do atendimento de propósito: ele e o wizard
              // escutam o Esc no document — fechar o PDF fecharia a campanha.
              <a
                href={headerMediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3 py-3 hover:bg-black/5"
                title="Abrir o documento numa nova aba"
              >
                <FileText size={22} className="flex-shrink-0 text-danger-500" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-[13px]">
                    {headerMediaName || 'Documento'}
                  </span>
                  <span className="block text-[11px] text-n-500">Documento do cabeçalho</span>
                </span>
                <span className="flex flex-shrink-0 items-center gap-1 font-semibold text-[12px] text-[#1f7aad]">
                  Abrir <ExternalLink size={12} />
                </span>
              </a>
            ) : (
              <div className="flex h-28 items-center justify-center text-[11px] text-n-500">
                {`[ ${headerFormat} ]`}
              </div>
            )}
          </div>
        )}
        <div className="p-3">
          {header && headerIsText && header.text && (
            <div className="mb-1.5 font-bold leading-snug">
              {renderVars(header.text, variables)}
            </div>
          )}
          {body?.text && (
            <div className="whitespace-pre-wrap leading-snug">
              {renderVars(body.text, variables)}
            </div>
          )}
          {footer?.text && <div className="mt-2 text-[11px] text-n-500">{footer.text}</div>}
        </div>
        {buttonsComp?.buttons && buttonsComp.buttons.length > 0 && (
          <div className="border-black/10 border-t">
            {buttonsComp.buttons.map((b, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: ordem estável do template
                key={i}
                className="flex items-center justify-center gap-1.5 border-black/5 border-t py-2 font-semibold text-[13px] text-[#1f7aad] first:border-t-0"
              >
                <span className="text-[12px]">{buttonIcon(b.type ?? '')}</span>
                {b.text ?? 'Botão'}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Renderiza o texto do template substituindo {{N}} pelo valor preenchido:
//  - fixo → o texto literal (chip verde)
//  - campo → «rótulo do campo» (chip azul — valor real é por destinatário)
//  - sem mapeamento ainda → {{N}} (chip âmbar)
function renderVars(text: string, variables: Record<string, VariableMapping>): React.ReactNode[] {
  const parts = text.split(/(\{\{\d+\}\})/g);
  return parts.map((part, i) => {
    const match = /^\{\{(\d+)\}\}$/.exec(part);
    if (!match) {
      // biome-ignore lint/suspicious/noArrayIndexKey: índice estável dentro do split
      return <span key={i}>{part}</span>;
    }
    const m = variables[match[1] as string];
    if (m?.kind === 'fixed' && m.value.trim()) {
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: idem
        <span key={i} className="rounded bg-success-100 px-1 text-success-800">
          {m.value}
        </span>
      );
    }
    if (m?.kind === 'field') {
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: idem
        <span key={i} className="rounded bg-brand-50 px-1 text-brand-700">
          «{fieldLabel(m.path)}»
        </span>
      );
    }
    return (
      <span
        // biome-ignore lint/suspicious/noArrayIndexKey: idem
        key={i}
        className="rounded bg-amber-100 px-1 font-mono text-[0.9em] text-amber-800"
      >
        {part}
      </span>
    );
  });
}
